import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, CheckCircle2, Circle } from "lucide-react";
import axios from "../../routers/axios"; // Sử dụng axios từ cấu hình chung

const QUESTION_TYPE_LABELS = {
  multiple_choice: "Multiple Choice",
  true_false_not_given: "True / False / Not Given",
  fill_in_blank: "Fill in the Blank",
  essay: "Essay",
};

/**
 * QuestionForm — dùng như modal.
 * Props:
 *   id?      — nếu có thì là chế độ sửa, nếu không là thêm mới
 *   onClose  — callback đóng modal (bắt buộc)
 */
export default function QuestionForm({ id, onClose }) {
  const [form, setForm] = useState({
    question_type: "multiple_choice",
    question_text: "",
    instruction: "",
    points: 1,
    correct_answer: "",
    answer_explanation: "",
    options: [],
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    if (id) {
      // Sử dụng axios để lấy chi tiết câu hỏi
      axios
        .get(`/questions/detail/${id}`)
        .then(({ data }) => {
          setForm({
            question_type: data.question_type ?? "multiple_choice",
            question_text: data.question_text ?? "",
            instruction: data.instruction ?? "",
            points: data.points ?? 1,
            correct_answer: data.correct_answer ?? "",
            answer_explanation: data.answer_explanation ?? "",
            options: data.options ?? [],
          });
          setLoaded(true);
        })
        .catch((err) => {
          console.error("Lỗi khi tải câu hỏi:", err);
          setLoaded(true);
        });
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addOption = () => {
    const nextKey = String.fromCharCode(65 + form.options.length); // A, B, C, D...
    setForm((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        {
          option_key: nextKey,
          option_text: "",
          is_correct: false,
          option_order: prev.options.length + 1,
        },
      ],
    }));
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleOptionChange = (index, field, value) => {
    setForm((prev) => {
      const newOptions = [...prev.options];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return { ...prev, options: newOptions };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (id) {
        // Cập nhật câu hỏi hiện có
        await axios.put(`/questions/update/${id}`, form);
      } else {
        // Tạo câu hỏi mới
        await axios.post(`/questions/create`, form);
      }
      onClose?.();
    } catch (err) {
      console.error("Lỗi khi submit:", err);
    } finally {
      setSaving(false);
    }
  };

  const showOptions = ["multiple_choice", "true_false_not_given"].includes(
    form.question_type
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-base font-bold shadow-sm">
            {id ? "✏️" : "＋"}
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-800 leading-tight">
              {id ? "Chỉnh sửa câu hỏi" : "Thêm câu hỏi mới"}
            </h2>
            {id && <p className="text-xs text-gray-400 mt-0.5">ID: #{id}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      {!loaded ? (
        <div className="flex-1 flex items-center justify-center py-20 text-sm text-gray-400">
          Đang tải dữ liệu…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          <div className="px-6 py-5 space-y-5 flex-1">

            {/* Nội dung câu hỏi */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Nội dung câu hỏi <span className="text-red-400">*</span>
              </label>
              <textarea
                name="question_text"
                value={form.question_text}
                onChange={handleChange}
                required
                rows={3}
                placeholder="Nhập nội dung câu hỏi..."
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition resize-none"
              />
            </div>

            {/* Hướng dẫn */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Hướng dẫn
              </label>
              <textarea
                name="instruction"
                value={form.instruction ?? ""}
                onChange={handleChange}
                rows={2}
                placeholder="Hướng dẫn thêm (tuỳ chọn)..."
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition resize-none"
              />
            </div>

            {/* Row: Loại câu hỏi + Điểm */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Loại câu hỏi
                </label>
                <select
                  name="question_type"
                  value={form.question_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition appearance-none cursor-pointer"
                >
                  {Object.entries(QUESTION_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Điểm
                </label>
                <input
                  type="number"
                  name="points"
                  value={form.points}
                  onChange={handleChange}
                  min={0}
                  step={0.5}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition"
                />
              </div>
            </div>

            {/* Đáp án đúng */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Đáp án đúng (key)
              </label>
              <input
                type="text"
                name="correct_answer"
                value={form.correct_answer ?? ""}
                onChange={handleChange}
                placeholder="VD: A, B, C, D..."
                maxLength={5}
                className="w-24 px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition uppercase"
              />
            </div>

            {/* Giải thích đáp án */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Giải thích đáp án
              </label>
              <textarea
                name="answer_explanation"
                value={form.answer_explanation ?? ""}
                onChange={handleChange}
                rows={2}
                placeholder="Giải thích tại sao đáp án đúng..."
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition resize-none"
              />
            </div>

            {/* Các lựa chọn */}
            {showOptions && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Các lựa chọn
                  </label>
                  <span className="text-xs text-gray-400">✓ = đáp án đúng</span>
                </div>

                <div className="space-y-2">
                  {form.options.map((opt, i) => (
                    <div
                      key={opt.id ?? i}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all ${
                        opt.is_correct
                          ? "border-green-300 bg-green-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      {/* Key label */}
                      <span className="shrink-0 w-5 text-xs font-bold text-gray-400">
                        {opt.option_key ?? String.fromCharCode(65 + i)}
                      </span>

                      {/* Toggle đúng/sai */}
                      <button
                        type="button"
                        onClick={() =>
                          handleOptionChange(i, "is_correct", !opt.is_correct)
                        }
                        className="shrink-0 text-gray-300 hover:text-green-500 transition-colors"
                      >
                        {opt.is_correct ? (
                          <CheckCircle2 size={17} className="text-green-500" />
                        ) : (
                          <Circle size={17} />
                        )}
                      </button>

                      {/* Nội dung lựa chọn */}
                      <input
                        type="text"
                        value={opt.option_text}
                        onChange={(e) =>
                          handleOptionChange(i, "option_text", e.target.value)
                        }
                        placeholder={`Nội dung lựa chọn ${opt.option_key ?? i + 1}...`}
                        className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none"
                      />

                      {/* Xóa */}
                      <button
                        type="button"
                        onClick={() => removeOption(i)}
                        className="shrink-0 text-gray-300 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addOption}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors w-full justify-center"
                >
                  <Plus size={13} />
                  Thêm lựa chọn
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl sticky bottom-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:scale-[.98] rounded-lg shadow-sm transition-all disabled:opacity-60 disabled:pointer-events-none flex items-center gap-2"
            >
              {saving && (
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {id ? "Lưu thay đổi" : "Tạo câu hỏi"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
